import type { SprintEntity } from '@data/utils/types';

import { productStore } from './productStore';
import { inVersion } from '../productMock';
import type { ProductVersion } from './productVersions';
import type { VersionTeam } from './budget';

/** How a version charges: hours at the rate card, hours at each person's cost, or one agreed total. */
export const PRICING_MODES = ['timeAndMaterials', 'atCost', 'fixedPrice'] as const;
export type PricingMode = (typeof PRICING_MODES)[number];

export interface VersionPricing {
  mode: PricingMode;
  /** Fixed price only: the agreed total, and how many of the version's sprints it is spread over. */
  total?: number;
  sprints?: number;
  /** At cost only: the percentage charged on top of each person's hourly cost. */
  markup?: number;
}

const TIME_AND_MATERIALS: VersionPricing = { mode: 'timeAndMaterials' };

export const pricingKey = (productId: string, version?: string): string =>
  inVersion(`productPricing:${productId}`, version);

const positive = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;

// Anything unreadable is time and materials, which is how every version was charged before this existed.
export const readPricing = (productId: string, version?: string): VersionPricing => {
  try {
    const stored: unknown = JSON.parse(productStore.getItem(pricingKey(productId, version)) ?? 'null');
    // A bare "atCost" is what the first cut of this stored.
    if (stored === 'atCost') return { mode: 'atCost' };
    const mode = (stored as VersionPricing | null)?.mode;
    if (mode === 'atCost') {
      const { markup } = stored as VersionPricing;
      return typeof markup === 'number' && Number.isFinite(markup) && markup >= 0 ? { mode, markup } : { mode };
    }
    if (mode === 'fixedPrice') {
      const { total, sprints } = stored as VersionPricing;
      return { mode, total: positive(total), sprints: positive(sprints) && Math.floor(sprints as number) };
    }
    return TIME_AND_MATERIALS;
  } catch {
    return TIME_AND_MATERIALS;
  }
};

/** Time and materials is the default, so choosing it removes the entry rather than storing it. */
export const writePricing = (productId: string, version: string | undefined, pricing: VersionPricing): void => {
  const key = pricingKey(productId, version);
  if (pricing.mode === 'timeAndMaterials') productStore.removeItem(key);
  else productStore.setItem(key, JSON.stringify(pricing));
};

/** The version's sprints in order, which is what a fixed price is spread over. */
export const sprintsOfVersion = (version: ProductVersion, sprints: SprintEntity[]): SprintEntity[] =>
  sprints
    .filter(
      sprint => sprint.startDate.slice(0, 10) >= version.startDate && sprint.startDate.slice(0, 10) <= version.endDate,
    )
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

/** Total over sprints in whole cents, the leftover cents on the last one so the parts add up to the total. */
export const fixedShare = (total: number, sprints: number, index: number): number => {
  if (index < 0 || index >= sprints) return 0;
  const cents = Math.round(total * 100);
  const each = Math.floor(cents / sprints);
  return (index === sprints - 1 ? cents - each * (sprints - 1) : each) / 100;
};

/** What a fixed-price version charges this sprint, or null where the version is not fixed price. */
export const fixedChargeFor = (
  version: ProductVersion,
  pricing: VersionPricing | undefined,
  sprintId: string,
  sprints: SprintEntity[],
): number | null => {
  if (pricing?.mode !== 'fixedPrice') return null;
  if (!pricing.total || !pricing.sprints) return 0;
  const index = sprintsOfVersion(version, sprints).findIndex(sprint => sprint.id === sprintId);
  return fixedShare(pricing.total, pricing.sprints, index);
};

/** The sprint's fixed charge under the terms it is priced by, or null where those terms are not fixed price. */
export const fixedChargeIn = (team: VersionTeam | null, sprintId: string, sprints: SprintEntity[]): number | null =>
  team ? fixedChargeFor(team.version, team.pricing, sprintId, sprints) : null;

/** An hour at cost, marked up: the person's cost plus the version's percentage, in whole cents. */
export const markedUp = (hourlyCost: number, markup = 0): number => Math.round(hourlyCost * (100 + markup)) / 100;
